import React, { useEffect, useRef, useState, useContext } from "react";
import styled from "styled-components";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { ProfileContext } from "../contexts/ProfileContext";
import { LoadingSpinner } from "../components/loading-spinner";
import { useReactToPrint } from "react-to-print";
import OrganizationNameButton from "../components/organization-name-button";
import * as XLSX from "xlsx";

ChartJS.register(ArcElement, Tooltip, Legend);

const ChevronDown = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const Disposition = () => {
  // ========== COMMON STATE ==========
  const [dataType, setDataType] = useState("ytd");
  const [exportOption, setExportOption] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState("");
  const [tempEndDate, setTempEndDate] = useState("");
  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });
  const [isLoading, setIsLoading] = useState(false);

  // ========== PAGE-SPECIFIC STATE ==========
  const [chartData, setChartData] = useState(null);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(true);

  // ========== REFS ==========
  const selectRef = useRef(null);
  const datePickerRef = useRef(null);
  const chartRef = useRef();

  // ========== CONTEXT ==========
  const {
    getDispositionReport,
    organizationFirebaseId, // Get Firebase ID instead of organizationNumber
    organization,
    worker,
  } = useContext(ProfileContext);

  // ========== DATE HELPERS ==========
  const formatDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  const getYTDRange = () => {
    const now = new Date();
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(firstDayOfYear), end: formatDate(now) };
  };

  const getDefaultMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(firstDay), end: formatDate(now) };
  };

  // ========== DATA FETCHING ==========
  const fetchDispositionData = async (
    type = "",
    startDate = "",
    endDate = "",
  ) => {
    try {
      setIsLoading(true);

      let result;

      if (type === "all") {
        const finalStart = "2015-01-01";
        const finalEnd = formatDate(new Date());
        result = await getDispositionReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          finalStart,
          finalEnd,
        );
        setSelectedRange({ start: finalStart, end: finalEnd });
      } else if (type === "ytd") {
        const ytd = getYTDRange();
        result = await getDispositionReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          ytd.start,
          ytd.end,
        );
        setSelectedRange(ytd);
      } else if (type === "monthly") {
        const def = getDefaultMonthRange();
        result = await getDispositionReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          def.start,
          def.end,
        );
        setSelectedRange(def);
      } else if (type === "custom") {
        if (!startDate || !endDate) {
          console.warn("Custom date range selected but no dates provided");
          return;
        }
        result = await getDispositionReport(
          organizationFirebaseId, // Pass Firebase ID
          "full",
          startDate,
          endDate,
        );
        setSelectedRange({ start: startDate, end: endDate });
      } else {
        // Default case - fetch without dates
        result = await getDispositionReport(organizationFirebaseId, "full"); // Pass Firebase ID
        setSelectedRange({ start: "", end: "" });
      }
      console.log(result);

      if (result.length > 0) {
        const labels = result.map((item) => item.disposition);
        const values = result.map((item) => item.total_count);
        const colors = ["#2DB3C0", "#C1DCDE", "#00323B", "#C2EDF6"].slice(
          0,
          labels.length,
        );

        setChartData({
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderWidth: 0,
            },
          ],
        });
      } else {
        setChartData({
          labels: ["No Data"],
          datasets: [
            { data: [1], backgroundColor: ["#E0E0E0"], borderWidth: 0 },
          ],
        });
      }
    } catch (error) {
      console.error("❌ Error fetching disposition report:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== INITIAL FETCH ==========
  useEffect(() => {
    // Only fetch if we have a Firebase ID
    if (organizationFirebaseId) {
      fetchDispositionData("ytd");
    }
  }, [organizationFirebaseId]); // Add organizationFirebaseId as dependency

  // ========== FILTER HANDLERS ==========
  const handleFilterChange = (type) => {
    setDataType(type);
    if (type === "custom") {
      setShowDatePicker(true);
    } else {
      fetchDispositionData(type);
    }
  };

  const handleApplyCustomDates = () => {
    if (!tempStartDate || !tempEndDate) {
      alert("Select both dates.");
      return;
    }
    setDataType("custom");
    setShowDatePicker(false);
    fetchDispositionData("custom", tempStartDate, tempEndDate);
  };

  const handleCancelCustomDates = () => {
    setTempStartDate("");
    setTempEndDate("");
    setShowDatePicker(false);
  };

  // ========== EXPORT FUNCTIONALITY ==========
  const handlePrint = useReactToPrint({
    content: () => chartRef.current,
    documentTitle: `Disposition_Report_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }`,
    onBeforePrint: () => {
      document.body.style.width = "100%";
    },
    onAfterPrint: () => {
      setExportOption("");
      document.body.style.width = "";
    },
  });

  const handleExportExcel = () => {
    if (!chartData || !chartData.labels?.length) {
      alert("No data available to export.");
      return;
    }

    const worksheetData = chartData.labels.map((label, i) => ({
      Disposition: label,
      "Total Calls": chartData.datasets[0].data[i],
    }));

    // Add total row to Excel export
    const totalCalls = chartData.datasets[0].data.reduce(
      (sum, value) => sum + value,
      0,
    );
    worksheetData.push({
      Disposition: "TOTAL",
      "Total Calls": totalCalls,
    });

    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Disposition Report");

    const fileName = `Disposition_Report_${selectedRange.start || "all"}_to_${
      selectedRange.end || "all"
    }.xlsx`;

    XLSX.writeFile(wb, fileName);
    setExportOption("");
  };

  useEffect(() => {
    if (exportOption === "print") handlePrint();
    if (exportOption === "export") handleExportExcel();
  }, [exportOption]);

  // ========== UI HANDLERS ==========
  const toggleBreakdown = () => {
    setIsBreakdownOpen(!isBreakdownOpen);
  };

  // ========== SELECT & DATEPICKER EFFECTS ==========
  useEffect(() => {
    const selectElement = selectRef.current;
    if (!selectElement) return;

    const handleMouseDown = () => setIsSelectOpen((prev) => !prev);
    const handleBlur = () => setIsSelectOpen(false);

    selectElement.addEventListener("mousedown", handleMouseDown);
    selectElement.addEventListener("blur", handleBlur);

    return () => {
      selectElement.removeEventListener("mousedown", handleMouseDown);
      selectElement.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  // ========== CHART OPTIONS ==========
  const doughnutOptions = {
    cutout: "60%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    maintainAspectRatio: true,
    responsive: true,
  };

  // ========== CALCULATIONS ==========
  const totalCalls =
    chartData?.datasets?.[0]?.data?.reduce((sum, value) => sum + value, 0) || 0;

  return (
    <Wrapper>
      {/* ========== STANDARDIZED TOP SECTION ========== */}
      <TopSection>
        {(worker && worker.role === "subscriber") ||
        organization?.name?.toLowerCase() === "crossroads" ? null : (
          <OrganizationNameButton />
        )}

        <TitleText>Disposition of Calls</TitleText>
      </TopSection>

      {/* ========== STANDARDIZED TOGGLE FILTERS ========== */}
      <ToggleButtonContainer>
        <ToggleContent>
          <ToggleButtonGroup>
            <ToggleButton
              active={dataType === "ytd"}
              onClick={() => handleFilterChange("ytd")}
            >
              YEAR TO DATE
            </ToggleButton>

            <ToggleButton
              active={dataType === "all"}
              onClick={() => handleFilterChange("all")}
            >
              ALL DATA
            </ToggleButton>

            <ToggleButton
              active={dataType === "monthly"}
              onClick={() => handleFilterChange("monthly")}
            >
              CURRENT MONTH
            </ToggleButton>

            <ToggleButton
              active={dataType === "custom"}
              onClick={() => {
                setDataType("custom");
                setShowDatePicker(true);
              }}
            >
              CUSTOM DATE RANGE
            </ToggleButton>
          </ToggleButtonGroup>

          <ExportContainer>
            <SelectWrapper>
              <ExportSelect
                ref={selectRef}
                value={exportOption}
                onChange={(e) => setExportOption(e.target.value)}
              >
                <option value="" disabled>
                  Export Options
                </option>
                <option value="print">Print PDF</option>
                <option value="export">Export as Excel Sheet</option>
              </ExportSelect>
              <ArrowIcon isOpen={isSelectOpen}>
                <ChevronDown />
              </ArrowIcon>
            </SelectWrapper>
          </ExportContainer>
        </ToggleContent>

        {showDatePicker && (
          <DatePickerDropdown ref={datePickerRef}>
            <DateInputGroup>
              <label>From</label>
              <input
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
              />
            </DateInputGroup>

            <DateInputGroup>
              <label>To</label>
              <input
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
              />
            </DateInputGroup>

            <DatePickerActions>
              <button onClick={handleApplyCustomDates}>Apply</button>
              <button onClick={handleCancelCustomDates}>Cancel</button>
            </DatePickerActions>
          </DatePickerDropdown>
        )}
      </ToggleButtonContainer>

      {/* ========== PAGE-SPECIFIC CONTENT ========== */}
      <printContainer ref={chartRef}>
        <PdfOnlyHeader>
          <div className="main-title">DISPOSITION OF CALLS</div>
        </PdfOnlyHeader>
        <Content>
          <LeftRightSection>
            <Left>
              <ChartWrapper>
                {isLoading ? (
                  <SpinnerWrapper>
                    <LoadingSpinner />
                  </SpinnerWrapper>
                ) : chartData?.labels?.length > 0 ? (
                  <Doughnut data={chartData} options={doughnutOptions} />
                ) : (
                  <p>No data available</p>
                )}
              </ChartWrapper>
            </Left>

            <Right>
              <BreakdownCard isOpen={isBreakdownOpen}>
                <BreakdownTitle onClick={toggleBreakdown}>
                  <ChevronIcon>
                    {isBreakdownOpen ? <ChevronDown /> : <ChevronRight />}
                  </ChevronIcon>
                  CALL BREAKDOWN
                </BreakdownTitle>

                {isBreakdownOpen && (
                  <BreakdownContent>
                    {isLoading ? (
                      <SpinnerWrapper>
                        <LoadingSpinner />
                      </SpinnerWrapper>
                    ) : chartData?.labels?.length > 0 ? (
                      <TableContainer>
                        <StyledTable>
                          <thead>
                            <tr>
                              <th>Color</th>
                              <th>Disposition</th>
                              <th>Total Calls</th>
                            </tr>
                          </thead>
                          <tbody>
                            {chartData?.labels.map((label, index) => (
                              <tr key={index}>
                                <td>
                                  <TableColorBox
                                    color={
                                      chartData.datasets[0].backgroundColor[
                                        index
                                      ]
                                    }
                                  />
                                </td>
                                <td className="text-capitalize">{label}</td>
                                <td>{chartData.datasets[0].data[index]}</td>
                              </tr>
                            ))}
                            {/* Total Row */}
                            <tr className="total-row">
                              <td
                                colSpan="2"
                                style={{
                                  textAlign: "left",
                                  fontWeight: "bold",
                                }}
                              >
                                Total
                              </td>
                              <td style={{ fontWeight: "bold" }}>
                                {totalCalls}
                              </td>
                            </tr>
                          </tbody>
                        </StyledTable>
                      </TableContainer>
                    ) : (
                      <p>No data available</p>
                    )}
                  </BreakdownContent>
                )}
              </BreakdownCard>
            </Right>
          </LeftRightSection>
        </Content>
      </printContainer>
    </Wrapper>
  );
};

export default Disposition;

// ========== STYLED COMPONENTS ==========

// Updated styled components with the new layout
const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const PdfOnlyHeader = styled.div`
  display: none;

  @media print {
    display: block;
    text-align: center;
    margin: 40px 0;
    padding-bottom: 20px;
    font-family: "Helvetica", "Arial", sans-serif;

    .main-title {
      text-align: center;
      font-family: Recoleta, serif;
      font-size: 1.25rem;
      font-weight: bold;
      color: #ec3310;
      margin-bottom: 0;
      text-decoration: underline;
      grid-column: 2;

      @media (max-width: 768px) {
        grid-column: 1;
        font-size: 1.1rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }
  }
`;

const ToggleButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
    padding: 0 1rem;
  }
`;

const ToggleContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  background: #eeeeee;
  border: 1px solid #e5e7eb;
  border-radius: 62px;
  overflow: hidden;

  @media (max-width: 768px) {
    flex-direction: column;
    border-radius: 12px;
    max-width: 300px;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  width: auto;
  min-width: 164px;
  height: 29px;
  padding: 0 12px;

  font-family: "Futura PT", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0.15em;
  text-align: center; /* Center alignment */

  border: none;
  border-radius: 62px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: ${({ active }) => (active ? "#2DB3C0" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#86CCDA")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 6px rgba(0, 0, 0, 0.15)" : "none"};

  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    height: 40px;

    font-size: 14px; /* Keep typography consistent */
    letter-spacing: 0.15em; /* Keep consistent */
    border-radius: 8px;
    margin: 2px 0;
  }

  @media (max-width: 480px) {
    height: 36px;

    font-size: 14px; /* Maintain required size */
    letter-spacing: 0.25em; /* Maintain required spacing */

    padding: 0 8px;
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 280px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    left: 50%;
    transform: translateX(-50%);
  }

  @media (max-width: 480px) {
    min-width: 260px;
    padding: 12px;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;

  label {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    font-family: "Space Grotesk";
  }

  input {
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    font-size: 13px;
    font-family: "Space Grotesk";
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #2db3c0;
      box-shadow: 0 0 0 2px rgba(45, 179, 192, 0.1);
    }
  }
`;

const DatePickerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;

  button {
    flex: 1;
    padding: 8px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-family: "Space Grotesk";
    font-weight: 600;
    border: none;
    transition: all 0.2s;

    &:first-child {
      background-color: #2db3c0;
      color: white;

      &:hover {
        background-color: #249aa5;
      }
    }

    &:last-child {
      background-color: #f3f4f6;
      color: #666;

      &:hover {
        background-color: #e5e7eb;
      }
    }
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 172px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 200px;
  }
`;

const ExportSelect = styled.select`
  appearance: none;
  background: #faf8f8;
  border: 1px solid #106271;
  color: #106271;
  font-family: "Space Grotesk";
  border-radius: 8px;
  width: 100%;
  height: 29px;
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s;
  padding: 0 10px;

  &:focus {
    outline: none;
  }
`;

const ArrowIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ isOpen }) => (isOpen ? "180deg" : "0deg")});
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.3s ease;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const TitleText = styled.h2`
  text-align: center;
  font-family: Recoleta, serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ec3310;
  margin-bottom: 0;
  text-decoration: underline;
  grid-column: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const printContainer = styled.div`
  width: 100%;
  display: block;
  @media print {
    width: 100%;
    display: block;
  }
`;

// Keep all your existing styled components below (they remain unchanged):
const Wrapper = styled.div`
  width: 100%;
  padding: 2rem 0;
  background-color: #f3f9fa;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding: 1.5rem 0;
  }

  @media (max-width: 768px) {
    padding: 1rem 0;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0;
  }
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 2rem;

  @media (max-width: 1024px) {
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const LeftRightSection = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  gap: 2rem;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 300px;

  @media (max-width: 1024px) {
    width: 100%;
    min-width: auto;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 300px;

  @media (max-width: 1024px) {
    width: 100%;
    min-width: auto;
  }
`;

const ChartWrapper = styled.div`
  width: 280px;
  height: 280px;

  @media (max-width: 1024px) {
    width: 250px;
    height: 250px;
  }

  @media (max-width: 768px) {
    width: 220px;
    height: 220px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 200px;
  }
`;

const BreakdownCard = styled.div`
  width: 100%;
  max-width: 700px;
  height: auto;
  margin: 0 auto;
  background: #fff;
  border-radius: 20px;
  border: 1px solid #dee2e6;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? "20px 30px" : "16px 30px")};
  transition: all 0.3s ease;

  @media (max-width: 1024px) {
    max-width: 600px;
    padding: ${({ isOpen }) => (isOpen ? "18px 25px" : "14px 25px")};
  }

  @media (max-width: 768px) {
    max-width: 500px;
    padding: ${({ isOpen }) => (isOpen ? "16px 20px" : "12px 20px")};
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    padding: ${({ isOpen }) => (isOpen ? "14px 16px" : "10px 16px")};
    border-radius: 12px;
  }
`;

const BreakdownTitle = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 700; /* Bold */
  font-style: normal;
  line-height: 100%;
  color: #2db3c0;
  letter-spacing: 0.25em; /* 25% spacing */
  text-align: center;

  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #249aa5;
  }

  @media (max-width: 480px) {
    font-size: 14px; /* Keeping typography consistent */
    letter-spacing: 0.25em; /* Keeping consistent */
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const BreakdownContent = styled.div`
  margin-top: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  margin-bottom: 20px;
  max-height: 350px;

  @media (max-width: 768px) {
    max-height: 300px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    max-height: 250px;
    margin-bottom: 12px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  font-family: "Space Grotesk", sans-serif;
  border-collapse: collapse;
  min-width: 300px;

  th,
  td {
    padding: 12px;
    border-bottom: 1px solid #dee2e6;
    white-space: nowrap;
  }

  thead th {
    background-color: #f2f6f7;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  .total-row {
    background-color: #f2f6f7;
    font-weight: bold;
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 10px 8px;
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    min-width: 250px;

    th,
    td {
      padding: 8px 6px;
      font-size: 13px;
    }
  }
`;

const TableColorBox = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${({ color }) => color};
  border-radius: 4px;
  border: 1px solid #ddd;

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
  }
`;
